import { useState, useRef, useEffect } from "react";
import { Pencil, Check, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

/**
 * Display-first field: renders the saved value as read-only text with a
 * pencil edit icon. Clicking the pencil (or the value) switches to an
 * input/textarea with Save (✓) and Cancel (✕); Enter saves, Escape
 * cancels. An empty value shows a muted "add …" prompt so the user still
 * has an affordance to start. onChange only fires on Save — editing is
 * local until committed, so a cancel discards the draft.
 *
 * This replaces always-on text boxes for fields that are usually just
 * being viewed (portal name, tagline), per the requested UX: show the
 * value, edit on demand.
 */
export default function EditableField({
  label,
  value,
  onChange,
  placeholder = "",
  emptyText = "Not set",
  multiline = false,
  required = false,
  testId,
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing && inputRef.current) inputRef.current.focus();
  }, [editing]);

  // Keep draft in sync if the external value changes while not editing
  // (e.g. config loads in after mount).
  useEffect(() => {
    if (!editing) setDraft(value || "");
  }, [value, editing]);

  const commit = () => {
    onChange(draft);
    setEditing(false);
  };
  const cancel = () => {
    setDraft(value || "");
    setEditing(false);
  };
  const onKeyDown = (e) => {
    if (e.key === "Enter" && !multiline) {
      e.preventDefault();
      commit();
    } else if (e.key === "Escape") {
      e.preventDefault();
      cancel();
    }
  };

  return (
    <div data-testid={testId ? `${testId}-field` : undefined}>
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">
          {label} {required && <span className="text-destructive">*</span>}
        </span>
        {!editing && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            aria-label={`Edit ${label}`}
            data-testid={testId ? `${testId}-edit` : undefined}
            className="flex items-center gap-1 text-xs font-medium text-primary hover:underline"
          >
            <Pencil className="h-3.5 w-3.5" />
            Edit
          </button>
        )}
      </div>

      {editing ? (
        <div className="mt-1.5 flex items-start gap-2">
          {multiline ? (
            <Textarea
              ref={inputRef}
              value={draft}
              placeholder={placeholder}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onKeyDown}
              data-testid={testId ? `${testId}-input` : undefined}
              className="flex-1"
            />
          ) : (
            <Input
              ref={inputRef}
              value={draft}
              placeholder={placeholder}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onKeyDown}
              data-testid={testId ? `${testId}-input` : undefined}
              className="flex-1"
            />
          )}
          <button type="button" onClick={commit} aria-label="Save" data-testid={testId ? `${testId}-save` : undefined}
            className="mt-1 rounded-md border border-input p-2 text-emerald-600 hover:bg-emerald-50">
            <Check className="h-4 w-4" />
          </button>
          <button type="button" onClick={cancel} aria-label="Cancel" data-testid={testId ? `${testId}-cancel` : undefined}
            className="mt-1 rounded-md border border-input p-2 text-muted-foreground hover:bg-muted">
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <p
          onClick={() => setEditing(true)}
          data-testid={testId ? `${testId}-value` : undefined}
          className={cn(
            "mt-1 cursor-text rounded-md border border-transparent px-0.5 py-1 text-sm",
            value ? "text-foreground" : "italic text-muted-foreground",
          )}
          title="Click to edit"
        >
          {value || emptyText}
        </p>
      )}
    </div>
  );
}
